import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Globe, Menu, X, Building2, Cpu, MapPin, Calendar, User, Briefcase, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectsPage = () => {
  const { language, toggleLanguage } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({
    company_name: 'Citra Abi Permana',
    logo_url: 'https://customer-assets.emergentagent.com/job_citrabi-tech/artifacts/1bg06gil_image.png'
  });
  const [filter, setFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, contactRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/contact-settings`)
      ]);
      
      setProjects(projectsRes.data);
      
      if (contactRes.data) {
        setCompanyInfo(contactRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(p => p.category === filter);

  const openProjectDetail = (project) => {
    setSelectedProject(project);
    setDetailOpen(true);
  };

  const closeProjectDetail = () => {
    setDetailOpen(false);
    setSelectedProject(null);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img src={companyInfo.logo_url} alt={companyInfo.company_name} className="h-10 lg:h-12 w-auto" />
              <span className="hidden sm:block font-heading font-bold text-gray-900 text-sm lg:text-base">
                {companyInfo.company_name}
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <Link to="/" className="text-gray-700 hover:text-emerald-600 font-medium transition-colors">
                {language === 'en' ? 'Home' : 'Beranda'}
              </Link>
              <Link to="/#about" className="text-gray-700 hover:text-emerald-600 font-medium transition-colors">
                {language === 'en' ? 'About Us' : 'Tentang Kami'}
              </Link>
              <Link to="/#services" className="text-gray-700 hover:text-emerald-600 font-medium transition-colors">
                {language === 'en' ? 'Services' : 'Layanan'}
              </Link>
              <span className="text-emerald-600 font-medium">
                {language === 'en' ? 'Projects' : 'Proyek'}
              </span>
              <Link to="/#contact" className="text-gray-700 hover:text-emerald-600 font-medium transition-colors">
                {language === 'en' ? 'Contact Us' : 'Hubungi Kami'}
              </Link>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-3">
              <button 
                onClick={toggleLanguage}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                <Globe className="w-4 h-4" />
                {language.toUpperCase()}
              </button>

              <Link to="/admin/login">
                <Button variant="default" className="hidden sm:flex bg-emerald-600 hover:bg-emerald-700 text-white">
                  {language === 'en' ? 'Admin Login' : 'Login Admin'}
                </Button>
              </Link>

              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-md hover:bg-gray-100"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100">
            <div className="px-6 py-4 space-y-4">
              <Link to="/" className="block text-gray-700 hover:text-emerald-600 font-medium" onClick={() => setMobileMenuOpen(false)}>
                {language === 'en' ? 'Home' : 'Beranda'}
              </Link>
              <span className="block text-emerald-600 font-medium">
                {language === 'en' ? 'Projects' : 'Proyek'}
              </span>
              <Link to="/admin/login" className="block" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                  {language === 'en' ? 'Admin Login' : 'Login Admin'}
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-emerald-600 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {language === 'en' ? 'Back to Home' : 'Kembali ke Beranda'}
          </Link>
          
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-gray-900 mb-4" data-testid="projects-title">
            {language === 'en' ? 'Our Projects' : 'Proyek Kami'}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl">
            {language === 'en' 
              ? 'Explore our portfolio of completed projects showcasing our expertise in construction and digital solutions.'
              : 'Jelajahi portofolio proyek kami yang telah selesai, menampilkan keahlian kami dalam konstruksi dan solusi digital.'}
          </p>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-full font-medium transition-colors ${
                filter === 'all' 
                  ? 'bg-emerald-600 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="filter-all"
            >
              {language === 'en' ? 'All Projects' : 'Semua Proyek'}
            </button>
            <button
              onClick={() => setFilter('construction')}
              className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
                filter === 'construction' 
                  ? 'bg-emerald-600 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="filter-construction"
            >
              <Building2 className="w-4 h-4" />
              {language === 'en' ? 'Construction' : 'Konstruksi'}
            </button>
            <button
              onClick={() => setFilter('it')}
              className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
                filter === 'it' 
                  ? 'bg-sky-600 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="filter-it"
            >
              <Cpu className="w-4 h-4" />
              {language === 'en' ? 'IT & Digital' : 'IT & Digital'}
            </button>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 bg-gray-50" data-testid="projects-grid">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
            </div>
          ) : filteredProjects.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <div 
                  key={project.id}
                  onClick={() => openProjectDetail(project)}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group cursor-pointer"
                  data-testid={`project-card-${index}`}
                >
                  <div className="relative h-56 overflow-hidden">
                    {project.image_url ? (
                      <img 
                        src={project.image_url} 
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                        {project.category === 'construction' ? (
                          <Building2 className="w-16 h-16 text-gray-300" />
                        ) : (
                          <Cpu className="w-16 h-16 text-gray-300" />
                        )}
                      </div>
                    )}
                    <div className="absolute top-4 left-4">
                      <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                        project.category === 'construction' 
                          ? 'bg-emerald-600 text-white' 
                          : 'bg-sky-600 text-white'
                      }`}>
                        {project.category === 'construction' 
                          ? (language === 'en' ? 'Construction' : 'Konstruksi')
                          : (language === 'en' ? 'IT & Digital' : 'IT & Digital')}
                      </span>
                    </div>
                    {/* View Detail Overlay */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="px-4 py-2 bg-white text-gray-900 rounded-full font-medium">
                        {language === 'en' ? 'View Details' : 'Lihat Detail'}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading font-bold text-xl text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-gray-600 line-clamp-2">
                      {project.description}
                    </p>
                    {(project.client || project.location || project.year) && (
                      <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-3 text-sm text-gray-500">
                        {project.client && (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {project.client}
                          </span>
                        )}
                        {project.year && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {project.year}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {language === 'en' ? 'No Projects Found' : 'Tidak Ada Proyek'}
              </h3>
              <p className="text-gray-500">
                {language === 'en' 
                  ? 'Check back later for updates to our portfolio.'
                  : 'Periksa kembali nanti untuk pembaruan portofolio kami.'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white mb-4">
            {language === 'en' ? 'Have a Project in Mind?' : 'Punya Proyek dalam Pikiran?'}
          </h2>
          <p className="text-emerald-100 mb-8 max-w-2xl mx-auto">
            {language === 'en' 
              ? "Let's discuss how we can help bring your vision to life."
              : 'Mari diskusikan bagaimana kami dapat membantu mewujudkan visi Anda.'}
          </p>
          <Link to="/#contact">
            <Button size="lg" variant="outline" className="bg-white text-emerald-600 hover:bg-emerald-50 border-white">
              {language === 'en' ? 'Contact Us' : 'Hubungi Kami'}
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <img src={companyInfo.logo_url} alt={companyInfo.company_name} className="h-10 w-auto" />
              <span className="font-heading font-bold">{companyInfo.company_name}</span>
            </div>
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {companyInfo.company_name}. {language === 'en' ? 'All rights reserved.' : 'Hak cipta dilindungi.'}
            </p>
          </div>
        </div>
      </footer>

      {/* Project Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0" data-testid="project-detail-modal">
          {selectedProject && (
            <>
              {/* Project Image */}
              <div className="relative h-64 sm:h-80">
                {selectedProject.image_url ? (
                  <img 
                    src={selectedProject.image_url} 
                    alt={selectedProject.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    {selectedProject.category === 'construction' ? (
                      <Building2 className="w-24 h-24 text-gray-300" />
                    ) : (
                      <Cpu className="w-24 h-24 text-gray-300" />
                    )}
                  </div>
                )}
                <div className="absolute top-4 left-4">
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                    selectedProject.category === 'construction' 
                      ? 'bg-emerald-600 text-white' 
                      : 'bg-sky-600 text-white'
                  }`}>
                    {selectedProject.category === 'construction' 
                      ? (language === 'en' ? 'Construction' : 'Konstruksi')
                      : (language === 'en' ? 'IT & Digital' : 'IT & Digital')}
                  </span>
                </div>
              </div>

              {/* Project Content */}
              <div className="p-6 sm:p-8">
                <h2 className="font-heading font-bold text-2xl sm:text-3xl text-gray-900 mb-4">
                  {selectedProject.title}
                </h2>

                {/* Project Meta */}
                {(selectedProject.client || selectedProject.location || selectedProject.year || selectedProject.scope) && (
                  <div className="grid sm:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
                    {selectedProject.client && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <User className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">{language === 'en' ? 'Client' : 'Klien'}</p>
                          <p className="font-medium text-gray-900">{selectedProject.client}</p>
                        </div>
                      </div>
                    )}
                    {selectedProject.location && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">{language === 'en' ? 'Location' : 'Lokasi'}</p>
                          <p className="font-medium text-gray-900">{selectedProject.location}</p>
                        </div>
                      </div>
                    )}
                    {selectedProject.year && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">{language === 'en' ? 'Year' : 'Tahun'}</p>
                          <p className="font-medium text-gray-900">{selectedProject.year}</p>
                        </div>
                      </div>
                    )}
                    {selectedProject.scope && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <Briefcase className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">{language === 'en' ? 'Scope' : 'Lingkup'}</p>
                          <p className="font-medium text-gray-900">{selectedProject.scope}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Description */}
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {language === 'en' ? 'Project Overview' : 'Gambaran Proyek'}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {selectedProject.description}
                  </p>
                </div>

                {/* Detailed Description */}
                {selectedProject.detail_description && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {language === 'en' ? 'Project Details' : 'Detail Proyek'}
                    </h3>
                    <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                      {selectedProject.detail_description}
                    </p>
                  </div>
                )}

                {/* Gallery */}
                {selectedProject.gallery_images && selectedProject.gallery_images.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">
                      {language === 'en' ? 'Project Gallery' : 'Galeri Proyek'}
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {selectedProject.gallery_images.map((img, idx) => (
                        <div key={idx} className="rounded-lg overflow-hidden h-32">
                          <img src={img} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Close Button */}
                <div className="mt-8 flex justify-end">
                  <Button onClick={closeProjectDetail} variant="outline">
                    {language === 'en' ? 'Close' : 'Tutup'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectsPage;
